/**
 *
 *Método para obtener el nombre del mes dado su número de mes.
 *
 */
export function obtenerNombreMes(numeroMes: number): string {
  const meses = [
    'Enero',
    'Febrero',
    'Marzo',
    'Abril',
    'Mayo',
    'Junio',
    'Julio',
    'Agosto',
    'Septiembre',
    'Octubre',
    'Noviembre',
    'Diciembre',
  ];
  return meses[numeroMes - 1]; // Los meses en el array comienzan desde 0
}

/**
 * Genera un listado de opciones de meses para componentes de selección.
 *
 * Cada opción contiene un valor numérico correspondiente al mes y un texto
 * traducido mediante el sistema de internacionalización de Vue.
 *
 * @param {Vue} instance - Instancia actual de Vue utilizada para acceder al método de traducción `$t`.
 *
 * @returns {Array<{ value: number; text: string }>} Lista de opciones de meses.
 *
 * @example
 * const meses = mesesOpciones(this);
 *
 * Resultado:
 * [
 *   { value: 1, text: "Enero" },
 *   { value: 2, text: "Febrero" },
 *   ...
 *   { value: 12, text: "Diciembre" }
 * ]
 */
export const mesesOpciones = (instance: Vue) => [
  { value: 1, text: instance.$t('meses.1') },
  { value: 2, text: instance.$t('meses.2') },
  { value: 3, text: instance.$t('meses.3') },
  { value: 4, text: instance.$t('meses.4') },
  { value: 5, text: instance.$t('meses.5') },
  { value: 6, text: instance.$t('meses.6') },
  { value: 7, text: instance.$t('meses.7') },
  { value: 8, text: instance.$t('meses.8') },
  { value: 9, text: instance.$t('meses.9') },
  { value: 10, text: instance.$t('meses.10') },
  { value: 11, text: instance.$t('meses.11') },
  { value: 12, text: instance.$t('meses.12') },
];

/**
 * Agrupa registros por mes y año, generando una lista única de periodos.
 *
 * El método utiliza una clave compuesta formada por mes y año para evitar
 * registros duplicados del mismo periodo.
 *
 * Cada elemento resultante contiene:
 * - Mes
 * - Año
 * - Descripción del periodo con el nombre del mes y año
 *
 * @param {any[]} datos - Lista de registros que contienen las propiedades `mes` y `anio`.
 *
 * @returns {Array<{ mes: number; anio: number; descripcion: string }>}
 * Lista de periodos agrupados sin duplicados.
 *
 * @example
 * const resultado = agruparMesAnio([
 *   { mes: 4, anio: 2025 },
 *   { mes: 4, anio: 2025 },
 *   { mes: 5, anio: 2025 }
 * ]);
 *
 * Resultado:
 * [
 *   {
 *     mes: 4,
 *     anio: 2025,
 *     descripcion: "Abril - 2025"
 *   },
 *   {
 *     mes: 5,
 *     anio: 2025,
 *     descripcion: "Mayo - 2025"
 *   }
 * ]
 */
export const agruparMesAnio = <T extends { mes?: number | null; anio?: number | null }>(datos: T[]): Array<T & { descripcion: string }> => {
  return Object.values(
    datos.reduce<Record<string, T & { descripcion: string }>>((acc, item) => {
      const key = `${item.mes}-${item.anio}`;

      if (!acc[key]) {
        acc[key] = {
          ...item,
          descripcion: `${obtenerNombreMes(item.mes)} - ${item.anio}`,
        };
      }

      return acc;
    }, {})
  );
};
